import { map, toNumber } from "lodash"
import { FC, ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { v4 } from "uuid"
import { cls } from "../../../Shared/cls"
import expandableIncrementorStyle from "../ExpandableIncrementor/ExpandableIncrementor.module.css"
import style from "./ExpandableDropdown.module.css"

type DropdownProps = {
	getItems: (indexes: number[], max?: number) => DropdownItem[]
	unSelectionItemText?: string
	onChange: (item: DropdownItem | null, componentId: string) => void
	defaultValue: number | string | null
	max?: number
	disabled?: boolean
	className?: string
	onInit?: () => void
}

export type DropdownItem = {
	key: string
	value: number | string
	disabled?: boolean
}

export const Dropdown: FC<DropdownProps> = ({
	getItems,
	onChange,
	unSelectionItemText,
	defaultValue,
	max,
	disabled,
	className,
	onInit,
}) => {
	const items = getItems([...Array(50).keys()], max)
	const [selectedItem, setSelectedItem] = useState<DropdownItem | null>(
		items.find((x) => x.value === defaultValue) || null,
	)

	const componentId = useRef(v4())

	useEffect(() => {
		if (onInit) {
			onInit()
		}
	}, [])

	return (
		<select
			disabled={disabled}
			style={{ cursor: disabled ? "not-allowed" : undefined }}
			value={selectedItem?.value}
			className={cls(style.dropdown, className)}
			onChange={(event) => {
				const value = !isNaN(toNumber(event.target.value)) ? toNumber(event.target.value) : event.target.value
				const item = items.find((x) => x.value === value) || null
				setSelectedItem(item)
				onChange(item, componentId.current)
			}}>
			{unSelectionItemText ? (
				<option key={v4()} value={0}>
					{unSelectionItemText}
				</option>
			) : null}
			{map(items, (item) => {
				return (
					<option disabled={item.disabled} key={v4()} value={item.value}>
						{item.key}
					</option>
				)
			})}
		</select>
	)
}

type ExpandableDropdownProps = {
	getItems: (indexes: number[], max?: number, minAllowedIndex?: number) => DropdownItem[]
	unSelectionItemText?: string
	onChange: (item: DropdownItem | null, componentId: string) => void
	defaultValue: number | string | null
	max?: number
	disabled?: boolean
	currentValue?: { value: number; show: boolean }
	className?: string
	minAllowedIndex?: number
	defaultToMinIndexItem?: boolean
	hideDisabledItems?: boolean
}

export type ExpandableDropdownRefProps = {
	id: string
	reset: () => void
}

export const ExpandableDropdown = forwardRef(
	(props: ExpandableDropdownProps, ref: ForwardedRef<ExpandableDropdownRefProps>) => {
		const items = props.getItems(
			[...Array(props.max !== undefined && props.minAllowedIndex !== undefined ? props.max : 50).keys()],
			props.max,
			props.minAllowedIndex,
		)
		const [selectedItem, setSelectedItem] = useState<DropdownItem | null>(
			items.find((x) => x.value === props.defaultValue) || null,
		)

		const componentId = useRef(v4())

		useEffect(() => {
			if (props.currentValue !== undefined) {
				setSelectedItem(items.find((x) => x.value === props.currentValue?.value) || null)
			}
		}, [props.currentValue])

		useImperativeHandle(
			ref,
			() => ({
				id: componentId.current,
				reset() {
					setSelectedItem(items.find((x) => x.value === props.defaultValue) || null)
				},
			}),
			[items, props],
		)

		return selectedItem ? (
			<select
				disabled={props.disabled}
				value={selectedItem.value}
				className={cls(style.dropdown, props.className)}
				onChange={(event) => {
					const value = !isNaN(toNumber(event.target.value)) ? toNumber(event.target.value) : event.target.value
					const item = items.find((x) => x.value === value) || null
					setSelectedItem(item)
					props.onChange(item, componentId.current)
				}}>
				{props.unSelectionItemText ? (
					<option key={v4()} value={0}>
						{props.unSelectionItemText}
					</option>
				) : null}
				{map(items, (item) => {
					if (props.hideDisabledItems && item.disabled) {
						return null
					}
					return (
						<option disabled={item.disabled} key={v4()} value={item.value}>
							{item.key}
						</option>
					)
				})}
			</select>
		) : (
			<button
				onClick={(event) => {
					event.preventDefault()
					event.stopPropagation()

					if (props.defaultToMinIndexItem) {
						if (props.minAllowedIndex !== undefined) {
							setSelectedItem(items[props.minAllowedIndex] || null)
							props.onChange(items[props.minAllowedIndex] || null, componentId.current)
						} else {
							setSelectedItem(items[0] || null)
							props.onChange(items[0] || null, componentId.current)
						}
					} else {
						setSelectedItem(items[0] || null)
						props.onChange(items[0] || null, componentId.current)
					}
				}}
				type="button"
				className={expandableIncrementorStyle.buttons}>
				+
			</button>
		)
	},
)
